/**
 * @module app.cd
 */

import { bindable } from 'aurelia-typed-observable-plugin';
import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { MemberService } from 'app/cd/services/member-service';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { openClaimSummary } from 'app/cd/components/summary/claim/claim-summary';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions'
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { debounce } from 'app/shared/lib/decorators';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { ClaimService, IClaimSearchFilters } from 'app/cd/services/claim-service';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class MemberClaims extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    @observable
    protected filterText: string = '';
    protected claimSearchFilters: IClaimSearchFilters = {};
    protected minSearchLength: number = 3;
    protected maxSearchLength: number = 200;

    public memberClaims: ReadonlyArray<CD.Claim> = [];

    protected claimDetails: Record<string, boolean> = {};

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    protected openProviderSummary = openProviderSummary;
    protected openClaimSummary = openClaimSummary;
    protected showProviderSummaryLink: boolean = false;
    protected showClaimsSummaryLink: boolean = false;

    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $table!: EssTableCustomElement<CD.Claim[]>;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly claimService: ClaimService,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);

        // Custom Sort
        this.sorting.entries = [
            {
                reference: 'receivedDate',
                direction: SortDirection.Descending
            }
        ];
    }

    protected bind(): void {
        this.checkPermissions();
        this.loadCustomFieldTargets();
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        // Close any current expanded details
        if(this.$table) this.$table.closeAll();
        this.claimDetails = {};
        if(this.claimSearchFilters.detailFilter) {
            await this.searchClaims();
            // expand all details when filter is used
            this.memberClaims.forEach(c => this.claimDetails[c.id!] = true);
            this.$table.openAll();
        } else {
            await this.loadMemberClaims();
        }
    }

    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(sorting.entries.length === 0) {
                this.sorting.entries = [
                    {
                        reference: 'receivedDate',
                        direction: SortDirection.Descending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    @debounce(600)
    protected async filterTextChanged(): Promise<void> {
        const search = this.filterText;
        this.claimDetails = {};

        // make sure minimum search length criteria is met
        if(search && search.length >= this.minSearchLength) {
            await this.searchClaims();
            // expand all details when filter is used
            this.memberClaims.forEach(c => this.claimDetails[c.id!] = true);
            this.$table.openAll();
        }
        // otherwise load unfiltered results and clear any potential previous filters
        else if(!search && this.claimSearchFilters.detailFilter) {
            this.clearResults();
            this.claimSearchFilters.detailFilter = null;
            await this.loadMemberClaims();
        }
    }

    protected clearResults(): void {
        this.memberClaims = [];
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
    }

    protected claimDetailLoaded(event: ExpandEvent<CD.Claim>): void {
        const item = event.detail.item;
        const items = event.detail.items;

        if(item && items.includes(item)) {
            this.claimDetails[item.id!] = true;
        }
    }

    ////****************** Functions ***********************************************////
    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.Claim');
    }

    private async checkPermissions(): Promise<void> {
        try {
            this.state.beginLoad();
            await this.accountContext.load();
        } catch(error) {
            throw error;
        } finally {
            this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
            this.showClaimsSummaryLink = this.accountContext.hasRouteAccess('cd.claim.summary');
            this.state.endLoad();
            this.reload();
        }
    }

    @apiCall
    private async searchClaims(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            top: this.pageSize,
            sorting: this.sorting,
            page: this.$pager?.page || 0
        };

        const includes: string[] = [
            'diagnoses',
            'metadata.created.byuser',
            'metadata.modified.byuser',
            'placeOfService',
            'provider.primary',
            'provider.primary.location',
            'provider.referring',
            'provider.referring.location',
            'services'
        ];

        const fields = [
            'amount.billed',
            'custom.lookup.*',
            'custom.text.*',
            'dateRange.end',
            'dateRange.start',
            'description',
            'diagnoses.code',
            'diagnoses.description',
            'lengthOfStay',
            'number',
            'metadata.created.byuser.description',
            'metadata.created.timestamp',
            'metadata.modified.byuser.description',
            'metadata.modified.timestamp',
            'paidDate',
            'placeOfService.description',
            'placeOfService.flag.*',
            'provider.primary.description',
            'provider.primary.location.description',
            'provider.referring.description',
            'provider.referring.location.description',
            'receivedDate',
            'services.code',
            'services.description'
        ];

        this.claimSearchFilters.memberId = this.memberId;
        this.claimSearchFilters.detailFilter = this.filterText;

        try {
            this.state.beginLoad();
            const response = await this.claimService.search(this.claimSearchFilters, options, includes, fields);
            this.memberClaims = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadMemberClaims(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes = [
            'metadata.created.byuser',
            'metadata.modified.byuser',
            'placeOfService',
            'provider.primary',
            'provider.primary.location',
            'provider.referring',
            'provider.referring.location'
        ];

        const fields = [
            'amount.billed',
            'custom.lookup.*',
            'custom.text.*',
            'dateRange.end',
            'dateRange.start',
            'description',
            'lengthOfStay',
            'number',
            'metadata.created.byuser.description',
            'metadata.created.timestamp',
            'metadata.modified.byuser.description',
            'metadata.modified.timestamp',
            'paidDate',
            'placeOfService.description',
            'placeOfService.flag.*',
            'provider.primary.description',
            'provider.primary.location.description',
            'provider.referring.description',
            'provider.referring.location.description',
            'receivedDate'
        ];

        try {
            this.state.beginLoad();
            const response = await this.memberService.loadClaimsByMember(this.memberId, options, includes, fields);
            this.memberClaims = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
